<template>
    <div>
        <!-- Import Button -->
        <button class="btn btn-outline-theme btn-sm" @click="openModal">
            <i class="bi bi-upload me-1"></i> Import Properties
        </button>

        <!-- Modal -->
        <div class="modal fade" id="importPropertiesModal" tabindex="-1" aria-hidden="true" ref="modalRef">
            <div class="modal-dialog modal-xl">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Import Properties</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>

                    <!-- Step 1: File Selection -->
                    <div v-if="step === 1" class="modal-body">
                        <div class="d-grid gap-3">
                            <!-- User Selection - only show if userId prop is not provided -->
                            <div v-if="!userId" class="">
                                <label class="form-label fw-medium">Select Business User <span class="text-danger">*</span></label>
                                <vue-multiselect
                                    v-model="selectedUser"
                                    :options="businessUsers"
                                    :searchable="true"
                                    :close-on-select="true"
                                    :show-labels="false"
                                    placeholder="Select a business user"
                                    label="name"
                                    track-by="id"
                                    :loading="loadingUsers">
                                    <template slot="noResult">No users found</template>
                                    <template slot="noOptions">No business users available</template>
                                </vue-multiselect>
                                <small class="text-muted">Properties will be imported for this user</small>
                            </div>
                            <!-- Show selected user info if userId prop is provided -->
                            <div v-else class="">
                                <div class="alert alert-info py-2 mb-0">
                                    <i class="bi bi-person-fill me-2"></i>
                                    Importing properties for: <strong>{{ userName || 'Selected User' }}</strong>
                                </div>
                            </div>
                            
                            <hr>
                            
                            <div class="row">
                                <div class="col-md-6">
                                    <div class="text-center p-3 border rounded h-100 d-flex flex-column justify-content-center">
                                        <p class="text-muted mb-3">Download the sample CSV file to see the required format</p>
                                        <button class="btn btn-outline-theme btn-sm" @click="downloadSample">
                                            <i class="bi bi-download me-2"></i>Download Sample CSV
                                        </button>
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <div class="upload-area p-3 border border-2 border-dashed rounded h-100" 
                                         @drop.prevent="handleDrop" 
                                         @dragover.prevent="isDragging = true"
                                         @dragleave="isDragging = false"
                                         :class="{ 'border-primary bg-light': isDragging }">
                                        <input type="file" ref="fileInput" @change="handleFileSelect" accept=".csv,.xlsx,.xls" class="d-none">
                                        <div class="text-center">
                                            <i class="bi bi-cloud-upload fs-2 text-muted"></i>
                                            <p class="mb-2 small">Drag and drop your CSV or Excel file here</p>
                                            <p class="text-muted small mb-2">or</p>
                                            <button class="btn btn-outline-theme btn-sm" @click="$refs.fileInput.click()" :disabled="!selectedUser">
                                                <i class="bi bi-folder2-open me-2"></i>Browse Files
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <p v-if="selectedFile" class="mt-3 text-success text-center">
                                <i class="bi bi-file-earmark-check me-1"></i>{{ selectedFile.name }}
                            </p>
                            <p v-if="!selectedUser" class="mt-2 text-danger small text-center">
                                <i class="bi bi-exclamation-triangle me-1"></i>Please select a business user first
                            </p>
                        </div>
                    </div>

                    <!-- Step 2: Column Mapping -->
                    <div v-if="step === 2" class="modal-body">
                        <div class="alert alert-info mb-3 p-2">
                            <i class="bi bi-info-circle me-2"></i>
                            Map your file columns to the corresponding database fields. At least one address field is required.
                        </div>
                        <div class="table-responsive" style="max-height: 600px; overflow-y: auto;">
                            <table class="table table-sm table-bordered">
                                <thead class="table-light sticky-top">
                                    <tr>
                                        <th style="width: 50%">CSV Column</th>
                                        <th style="width: 50%">Map To Field</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(column, index) in csvHeaders" :key="index">
                                        <td>
                                            <span class="fw-medium">{{ column }}</span>
                                            <br>
                                            <small class="text-muted">{{ getPreviewValue(column) }}</small>
                                        </td>
                                        <td>
                                            <vue-multiselect
                                                v-model="columnMapping[column]"
                                                :options="getAvailableOptions(column)"
                                                :searchable="true"
                                                :close-on-select="true"
                                                :show-labels="false"
                                                placeholder="-- Skip Column --"
                                                :allow-empty="true">
                                            </vue-multiselect>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div class="mt-3">
                            <small class="text-muted">
                                <i class="bi bi-info-circle me-1"></i>
                                Found {{ csvData.length }} records to import
                            </small>
                        </div>
                    </div>

                    <!-- Step 3: Import Progress -->
                    <div v-if="step === 3" class="modal-body">
                        <div class="text-center py-4">
                            <div v-if="!importComplete" class="mb-4">
                                <div class="alert alert-warning py-2 mb-3">
                                    <i class="bi bi-exclamation-triangle me-2"></i>
                                    <strong>Do not close this window while importing</strong>
                                </div>
                                <div class="spinner-border text-primary mb-3" role="status">
                                    <span class="visually-hidden">Loading...</span>
                                </div>
                                <h5>Importing Properties...</h5>
                            </div>
                            <div v-else class="mb-4">
                                <i class="bi bi-check-circle-fill text-success fs-1"></i>
                                <h5 class="mt-2">Import Complete!</h5>
                            </div>
                            
                            <div class="progress mb-3" style="height: 20px;">
                                <div class="progress-bar progress-bar-striped" 
                                     :class="{ 'progress-bar-animated': !importComplete, 'bg-success': importComplete }"
                                     role="progressbar" 
                                     :style="{ width: progress + '%' }">
                                    {{ progress }}%
                                </div>
                            </div>
                            
                            <div class="d-flex justify-content-center gap-4 text-center">
                                <div>
                                    <h4 class="text-success mb-0">{{ importedCount }}</h4>
                                    <small class="text-muted">Imported</small>
                                </div>
                                <div>
                                    <h4 class="text-danger mb-0">{{ errorCount }}</h4>
                                    <small class="text-muted">Errors</small>
                                </div>
                                <div>
                                    <h4 class="text-primary mb-0">{{ totalCount }}</h4>
                                    <small class="text-muted">Total</small>
                                </div>
                            </div>
                            
                            <div v-if="errorCount > 0" class="mt-4">
                                <button class="btn btn-sm btn-outline-danger" @click="showErrors = !showErrors">
                                    <i class="bi bi-exclamation-triangle me-1"></i>
                                    {{ showErrors ? 'Hide' : 'Show' }} Errors ({{ errorCount }})
                                </button>
                                <div v-if="showErrors" class="mt-3 text-start">
                                    <div class="alert alert-danger" style="max-height: 150px; overflow-y: auto;">
                                        <ul class="mb-0 ps-3">
                                            <li v-for="(error, index) in errors" :key="index" class="small">
                                                {{ error }}
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button v-if="step === 1" type="button" class="btn btn-outline-danger btn-sm" data-bs-dismiss="modal">
                            Cancel
                        </button>
                        
                        <button v-if="step === 2" type="button" class="btn btn-secondary btn-sm" @click="step = 1">
                            <i class="bi bi-arrow-left me-1"></i>Back
                        </button>
                        <button v-if="step === 2" type="button" class="btn btn-theme btn-sm" 
                                @click="startImport" :disabled="!isMappingValid">
                            <i class="bi bi-upload me-1"></i>Start Import
                        </button>
                        
                        <button v-if="step === 3 && importComplete" type="button" class="btn btn-theme btn-sm" @click="closeAndRefresh">
                            <i class="bi bi-check me-1"></i>Done
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ImportPropertiesModal',
    
    props: {
        importRoute: {
            type: String,
            required: true
        },
        businessUsersRoute: {
            type: String,
            default: '/api/admin/business-users'
        },
        userId: {
            type: [Number, String],
            default: null
        },
        userName: {
            type: String,
            default: null
        }
    },
    
    data() {
        return {
            step: 1,
            isDragging: false,
            selectedFile: null,
            csvHeaders: [],
            csvData: [],
            columnMapping: {},
            progress: 0,
            importedCount: 0,
            contactPersonsCount: 0,
            errorCount: 0,
            totalCount: 0,
            errors: [],
            showErrors: false,
            importComplete: false,
            modal: null,
            selectedUser: null,
            businessUsers: [],
            loadingUsers: false,
            dbFields: [
                { key: 'address_line_one', label: 'Address Line 1' },
                { key: 'address_line_two', label: 'Address Line 2' },
                { key: 'city', label: 'City' },
                { key: 'state', label: 'State' },
                { key: 'county', label: 'County' },
                { key: 'country', label: 'Country' },
                { key: 'postal_code', label: 'Postal Code' },
                { key: 'address_note', label: 'Address Note' },
                { key: 'tenant_title', label: 'Tenant Title' },
                { key: 'tenant_first_name', label: 'Tenant First Name' },
                { key: 'tenant_last_name', label: 'Tenant Last Name' },
                { key: 'tenant_email', label: 'Tenant Email' },
                { key: 'tenant_phone_one', label: 'Tenant Phone Number 1' },
                { key: 'tenant_phone_two', label: 'Tenant Phone Number 2' }
            ]
        }
    },
    
    computed: {
        isMappingValid() {
            // Check if at least one address field is mapped (using labels now)
            const addressLabels = ['Address Line 1', 'Address Line 2', 'City', 'State', 'County', 'Country', 'Postal Code'];
            return addressLabels.some(label => Object.values(this.columnMapping).includes(label));
        },
        dbFieldOptions() {
            // Add 'Skip Column' as first option
            return ['-- Skip Column --', ...this.dbFields.map(f => f.label)];
        }
    },
    
    mounted() {
        this.modal = new bootstrap.Modal(this.$refs.modalRef);
        // If userId prop is provided, set selectedUser directly
        if (this.userId) {
            this.selectedUser = { id: this.userId, name: this.userName || 'Selected User' };
        } else {
            this.loadBusinessUsers();
        }
    },
    
    methods: {
        openModal() {
            this.resetState();
            this.modal.show();
        },
        
        resetState() {
            this.step = 1;
            this.selectedFile = null;
            this.csvHeaders = [];
            this.csvData = [];
            this.columnMapping = {};
            this.progress = 0;
            this.importedCount = 0;
            this.errorCount = 0;
            this.totalCount = 0;
            this.errors = [];
            this.showErrors = false;
            this.importComplete = false;
            // Reset selectedUser only if userId prop is not provided
            if (this.userId) {
                this.selectedUser = { id: this.userId, name: this.userName || 'Selected User' };
            } else {
                this.selectedUser = null;
            }
        },
        
        async loadBusinessUsers() {
            this.loadingUsers = true;
            try {
                const response = await axios.get(this.businessUsersRoute);
                this.businessUsers = response.data || [];
            } catch (error) {
                console.error('Failed to load business users:', error);
                this.$toast.error('Failed to load business users');
            } finally {
                this.loadingUsers = false;
            }
        },
        
        downloadSample() {
            const headers = [
                'Address Line 1',
                'Address Line 2',
                'City',
                'State',
                'County',
                'Country',
                'Postal Code',
                'Address Note',
                'Tenant Title',
                'Tenant First Name',
                'Tenant Last Name',
                'Tenant Email',
                'Tenant Phone One',
                'Tenant Phone Two'
            ];
            
            const sampleData = [
                '123 Main Street',
                'Apt 4B',
                'London',
                'Greater London',
                'Westminster',
                'United Kingdom',
                'SW1A 1AA',
                'Main entrance on the left',
                'Mr',
                'John',
                'Smith',
                'john.smith@example.com',
                '+44 20 7946 0958',
                '+44 20 7946 0959'
            ];
            
            const csvContent = headers.join(',') + '\n' + sampleData.join(',');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'property_import_sample.csv';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        },
        
        handleFileSelect(event) {
            if (!this.selectedUser) {
                this.$toast.error('Please select a business user first');
                return;
            }
            const file = event.target.files[0];
            if (file) {
                this.selectedFile = file;
                this.processFile(file);
            }
        },
        
        handleDrop(event) {
            this.isDragging = false;
            if (!this.selectedUser) {
                this.$toast.error('Please select a business user first');
                return;
            }
            const file = event.dataTransfer.files[0];
            const validExtensions = ['.csv', '.xlsx', '.xls'];
            const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
            if (file && validExtensions.includes(fileExtension)) {
                this.selectedFile = file;
                this.processFile(file);
            } else {
                this.$toast.error('Please upload a CSV or Excel file');
            }
        },
        
        processFile(file) {
            const fileExtension = file.name.split('.').pop().toLowerCase();
            
            if (fileExtension === 'csv') {
                const reader = new FileReader();
                reader.onload = (e) => {
                    const text = e.target.result;
                    this.parseCSV(text);
                };
                reader.readAsText(file);
            } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
                this.parseExcel(file);
            }
        },
        
        async parseExcel(file) {
            try {
                // Use SheetJS library loaded from CDN
                const data = await file.arrayBuffer();
                const workbook = XLSX.read(data, { type: 'array' });
                const firstSheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[firstSheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
                
                if (jsonData.length < 2) {
                    this.$toast.error('Excel file must have headers and at least one data row');
                    return;
                }
                
                // First row is headers
                this.csvHeaders = jsonData[0].map(h => String(h || '').trim());
                
                // Rest are data rows
                this.csvData = [];
                for (let i = 1; i < jsonData.length; i++) {
                    const rowValues = jsonData[i];
                    if (rowValues && rowValues.some(v => v !== null && v !== undefined && v !== '')) {
                        const row = {};
                        this.csvHeaders.forEach((header, index) => {
                            row[header] = String(rowValues[index] || '').trim();
                        });
                        this.csvData.push(row);
                    }
                }
                
                // Auto-map columns
                this.autoMapColumns();
                
                // Move to step 2
                this.step = 2;
            } catch (error) {
                console.error('Excel parsing error:', error);
                this.$toast.error('Failed to parse Excel file. Please check the file format.');
            }
        },
        
        parseCSV(text) {
            const lines = text.split(/\r?\n/);
            if (lines.length < 2) {
                this.$toast.error('CSV file must have headers and at least one data row');
                return;
            }
            
            // Parse headers
            this.csvHeaders = this.parseCSVLine(lines[0]);
            
            // Parse data rows
            this.csvData = [];
            for (let i = 1; i < lines.length; i++) {
                const line = lines[i].trim();
                if (line) {
                    const values = this.parseCSVLine(line);
                    const row = {};
                    this.csvHeaders.forEach((header, index) => {
                        row[header] = values[index] || '';
                    });
                    this.csvData.push(row);
                }
            }
            
            // Auto-map columns based on similar names
            this.autoMapColumns();
            
            // Move to step 2
            this.step = 2;
        },
        
        parseCSVLine(line) {
            const result = [];
            let current = '';
            let inQuotes = false;
            
            for (let i = 0; i < line.length; i++) {
                const char = line[i];
                
                if (char === '"') {
                    inQuotes = !inQuotes;
                } else if (char === ',' && !inQuotes) {
                    result.push(current.trim());
                    current = '';
                } else {
                    current += char;
                }
            }
            result.push(current.trim());
            
            return result;
        },
        
        autoMapColumns() {
            // Map CSV header to db field label
            const mappings = {
                'address line one': 'Address Line 1',
                'address line 1': 'Address Line 1',
                'address_1': 'Address Line 1',
                'address1': 'Address Line 1',
                'address': 'Address Line 1',
                'street': 'Address Line 1',
                'address line two': 'Address Line 2',
                'address line 2': 'Address Line 2',
                'address_2': 'Address Line 2',
                'address2': 'Address Line 2',
                'address line three': 'City',
                'address line 3': 'City',
                'address_3': 'City',
                'address3': 'City',
                'city': 'City',
                'town': 'City',
                'address line four': 'County',
                'address line 4': 'County',
                'address_4': 'County',
                'address4': 'County',
                'state': 'State',
                'province': 'State',
                'county': 'County',
                'country': 'Country',
                'postal code': 'Postal Code',
                'postcode': 'Postal Code',
                'post code': 'Postal Code',
                'zip': 'Postal Code',
                'zip code': 'Postal Code',
                'zipcode': 'Postal Code',
                'address note': 'Address Note',
                'notes': 'Address Note',
                'note': 'Address Note',
                'tenant title': 'Tenant Title',
                'title': 'Tenant Title',
                'salutation': 'Tenant Title',
                'tenant first name': 'Tenant First Name',
                'first name': 'Tenant First Name',
                'firstname': 'Tenant First Name',
                'forename': 'Tenant First Name',
                'tenant last name': 'Tenant Last Name',
                'last name': 'Tenant Last Name',
                'lastname': 'Tenant Last Name',
                'surname': 'Tenant Last Name',
                'tenant email': 'Tenant Email',
                'email': 'Tenant Email',
                'e-mail': 'Tenant Email',
                'tenant phone one': 'Tenant Phone Number 1',
                'tenant phone 1': 'Tenant Phone Number 1',
                'mobile': 'Tenant Phone Number 1',
                'phone': 'Tenant Phone Number 1',
                'phone number': 'Tenant Phone Number 1',
                'telephone': 'Tenant Phone Number 1',
                'phone no': 'Tenant Phone Number 2',
                'phone no.': 'Tenant Phone Number 2',
                'tenant phone two': 'Tenant Phone Number 2',
                'tenant phone 2': 'Tenant Phone Number 2',
                'phone 2': 'Tenant Phone Number 2',
                'phone number 2': 'Tenant Phone Number 2',
                'alternate phone': 'Tenant Phone Number 2',
            };
            
            this.csvHeaders.forEach(header => {
                const normalizedHeader = header.toLowerCase().trim();
                if (mappings[normalizedHeader]) {
                    this.columnMapping[header] = mappings[normalizedHeader];
                } else {
                    this.columnMapping[header] = null;
                }
            });
        },
        
        getPreviewValue(column) {
            if (this.csvData.length > 0) {
                const value = this.csvData[0][column];
                if (value && value.length > 30) {
                    return value.substring(0, 30) + '...';
                }
                return value || '(empty)';
            }
            return '';
        },
        
        isFieldMapped(fieldLabel, currentColumn) {
            // Check if this field is already mapped to another column
            for (const [col, mappedLabel] of Object.entries(this.columnMapping)) {
                if (mappedLabel === fieldLabel && col !== currentColumn) {
                    return true;
                }
            }
            return false;
        },
        
        getAvailableOptions(currentColumn) {
            // Return options that are not already mapped to other columns
            return this.dbFieldOptions.filter(label => {
                return !this.isFieldMapped(label, currentColumn);
            });
        },
        
        async startImport() {
            this.step = 3;
            this.totalCount = this.csvData.length;
            this.importedCount = 0;
            this.contactPersonsCount = 0;
            this.errorCount = 0;
            this.errors = [];
            this.progress = 0;
            this.importComplete = false;
            
            // Convert label-based mapping to key-based mapping for backend
            const keyMapping = {};
            for (const [csvCol, label] of Object.entries(this.columnMapping)) {
                if (label && label !== '-- Skip Column --') {
                    const field = this.dbFields.find(f => f.label === label);
                    if (field) {
                        keyMapping[csvCol] = field.key;
                    }
                }
            }
            
            // Increased batch size for better performance with large imports
            const batchSize = 100;
            let processedCount = 0;
            let retryCount = 0;
            const maxRetries = 3;
            
            for (let i = 0; i < this.csvData.length; i += batchSize) {
                const batch = this.csvData.slice(i, i + batchSize);
                let success = false;
                retryCount = 0;
                
                while (!success && retryCount < maxRetries) {
                    try {
                        const response = await axios.post(this.importRoute, {
                            batch: batch,
                            mapping: keyMapping,
                            user_id: this.selectedUser ? this.selectedUser.id : null
                        }, {
                            timeout: 120000 // 2 minute timeout for large batches
                        });
                        
                        this.importedCount += response.data.imported || 0;
                        this.contactPersonsCount += response.data.contact_persons || 0;
                        this.errorCount += response.data.error_count || 0;
                        if (response.data.errors && response.data.errors.length > 0) {
                            // Only keep last 100 errors for display
                            const newErrors = response.data.errors;
                            this.errors.push(...newErrors);
                            if (this.errors.length > 100) {
                                this.errors = this.errors.slice(-100);
                            }
                        }
                        success = true;
                    } catch (error) {
                        retryCount++;
                        if (retryCount >= maxRetries) {
                            const errorMsg = error.response?.data?.message || `Batch ${Math.floor(i/batchSize) + 1} failed after ${maxRetries} retries`;
                            this.errors.push(errorMsg);
                        } else {
                            // Wait before retry
                            await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
                        }
                    }
                }
                
                processedCount += batch.length;
                this.progress = Math.round((processedCount / this.totalCount) * 100);
                
                // Minimal delay between batches
                await new Promise(resolve => setTimeout(resolve, 50));
            }
            
            this.importComplete = true;
        },
        
        closeAndRefresh() {
            this.modal.hide();
            // Emit event to parent to refresh the data table
            this.$emit('import-complete', {
                imported: this.importedCount,
                errors: this.errors.length
            });
            // Reload the page to refresh the data table
            window.location.reload();
        }
    }
}
</script>

<style scoped>
.upload-area {
    transition: all 0.3s ease;
    cursor: pointer;
}

.upload-area:hover {
    border-color: #0d6efd !important;
    background-color: #f8f9fa;
}

.border-dashed {
    border-style: dashed !important;
}

.sticky-top {
    position: sticky;
    top: 0;
    z-index: 1;
}
</style>
